// Vendor debt payment-proof viewer — full-screen overlay. Read-only: shows
// the proof files attached to a vendor debt ledger entry at full size (with
// prev/next between multiple files). No upload, no remove. Reuses the
// generic tbl_attachment fetch via getVendorDebtAttachmentAction
// (entityTypeId = 7, "Vendor Debt Attachment" in tbl_entityType) so the
// existing GraphQL/saga/reducer wiring stays
// untouched. Mirrors VendorBillAttachmentViewer.tsx.
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { isArrayNotEmpty } from "../../Utils/helpers";
import { getVendorDebtAttachmentAction } from "../../Store/actions/apparelAction";

const IMG_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}vendor/debt/proof/`;

const isPdf = (mimeType?: string, name?: string) =>
    mimeType === "application/pdf" || name?.split(".").pop()?.toLowerCase() === "pdf";

export interface VendorDebtAttachmentViewerProps {
    open: boolean;
    onClose: () => void;
    entityId: any;
}

export function VendorDebtAttachmentViewer({ open, onClose, entityId }: VendorDebtAttachmentViewerProps) {
    const dispatch = useDispatch();
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { getVendorDebtAttachmentData } = apparelReducerState;
    const proofFiles = getVendorDebtAttachmentData?.getVendorDebtAttachment;
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (open && entityId) dispatch(getVendorDebtAttachmentAction({ entityId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, entityId]);

    useEffect(() => {
        setActiveIndex(0);
    }, [proofFiles]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") setActiveIndex((i) => Math.max(0, i - 1));
            if (e.key === "ArrowRight") setActiveIndex((i) => Math.min((proofFiles?.length || 1) - 1, i + 1));
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, proofFiles, onClose]);

    if (!open) return null;

    const total = proofFiles?.length || 0;
    const currentItem = proofFiles?.[activeIndex];
    const currentSrc = isArrayNotEmpty(proofFiles) ? `${IMG_BASE}${currentItem?.fileUuid}` : "";
    const currentIsPdf = isPdf(currentItem?.mimeType, currentItem?.fileUuid);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-6"
        >
            <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between px-6 py-4">
                <span className="text-sm font-medium text-white">Payment Proof</span>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close viewer"
                    className="rounded-md bg-background/80 p-1.5 text-foreground hover:bg-background"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            {isArrayNotEmpty(proofFiles) ? (
                <>
                    {currentIsPdf ? (
                        <iframe
                            src={currentSrc}
                            title="payment proof PDF"
                            className="h-[85vh] w-[90vw] rounded-md bg-white"
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        // eslint-disable-next-line jsx-a11y/img-redundant-alt, jsx-a11y/no-noninteractive-element-interactions
                        <img
                            src={currentSrc}
                            alt="payment proof full size"
                            className="max-h-full max-w-full rounded-md object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    {total > 1 && (
                        <>
                            <button
                                type="button"
                                aria-label="Previous file"
                                disabled={activeIndex === 0}
                                onClick={(e) => { e.stopPropagation(); setActiveIndex((i) => Math.max(0, i - 1)); }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-2 text-foreground hover:bg-background disabled:opacity-40"
                            >
                                <ChevronLeft className="h-6 w-6" />
                            </button>
                            <button
                                type="button"
                                aria-label="Next file"
                                disabled={activeIndex === total - 1}
                                onClick={(e) => { e.stopPropagation(); setActiveIndex((i) => Math.min(total - 1, i + 1)); }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-2 text-foreground hover:bg-background disabled:opacity-40"
                            >
                                <ChevronRight className="h-6 w-6" />
                            </button>
                            <span className="absolute bottom-4 rounded-full bg-background/80 px-3 py-1 text-sm font-medium text-foreground">
                                {activeIndex + 1} / {total}
                            </span>
                        </>
                    )}
                </>
            ) : (
                <p className="text-sm text-muted-foreground">No payment proof uploaded yet.</p>
            )}
        </div>
    );
}

export default VendorDebtAttachmentViewer;
